import React from 'react';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { ExpandedRowOrderItemDisplayProps } from './types';
import {
  ExpandableRowCellDetailTextSx,
  ExpandableRowCellTrackingLinkSx,
  ExpandedRowCellHeaderTextSx,
  ExpandedTableRowSx,
  OrderItemTableSx,
  ExpandableProductTitleRowCellTextSx,
  ShipmentPillSx,
  FailedPillTypeSx,
  ExpandedContainerSx,
  ShipmentStatusDescriptionSx,
} from './styles';
import { getItemProductTitle } from '../../utils';
import { ShipmentStatusPill } from 'components';
import { getCarrierName, sortOrderItemsByNumber, getAssetLink } from './utils';
import { ShipmentStatus, ShipmentStatuses } from 'globals/constants/shipment';
import { getShipmentInfo } from 'pages/Shipping/utils';
import moment from 'moment';
import { OrderType } from 'global-constants';

const ExpandedRowOrderItemDisplay = (
  props: ExpandedRowOrderItemDisplayProps
) => {
  const { detailsPage } = props;
  const sortedOrderItems = sortOrderItemsByNumber(props.orderItems);
  const hasNoShipping =
    props.order.orderType.name === OrderType.RESELL ||
    props.order.orderType.name === OrderType.RECYCLE ||
    props.order.orderType.name === OrderType.REPAIR ||
    props.order.orderType.name === OrderType.PROCESS;
  const isRetrieval = [
    OrderType.BULK_RETRIEVAL,
    OrderType.DROP_RETRIEVAL,
    OrderType.RETRIEVAL,
  ].some(
    (retrievalType: string) =>
      retrievalType === (props.order?.orderType?.name ?? '')
  );

  return (
    <Container sx={ExpandedContainerSx}>
      <Table sx={OrderItemTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Order Item Number
              </Typography>
            </TableCell>
            <TableCell sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Tracking
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Title
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Serial Number
              </Typography>
            </TableCell>
            {(detailsPage || hasNoShipping) && (
              <TableCell>
                <Typography
                  color="colors.gray94"
                  sx={ExpandedRowCellHeaderTextSx}
                >
                  Asset Number
                </Typography>
              </TableCell>
            )}
            <TableCell sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Carrier
              </Typography>
            </TableCell>
            <TableCell sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Shipment Status
              </Typography>
            </TableCell>
            <TableCell sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                Estimated delivery
              </Typography>
            </TableCell>
            <TableCell sx={{ display: !hasNoShipping ? 'none' : 'table-cell' }}>
              <Typography
                color="colors.gray94"
                sx={ExpandedRowCellHeaderTextSx}
              >
                &nbsp;
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedOrderItems.map((orderItem) => {
            const shipmentInfo = getShipmentInfo(orderItem);
            const isDelivered =
              shipmentInfo.status === ShipmentStatuses.DELIVERED;
            const shipmentStatus =
              orderItem.shipmentStatus || shipmentInfo.status || 'PENDING';
            return (
              <TableRow sx={ExpandedTableRowSx}>
                <TableCell>
                  <Typography
                    color="colors.black"
                    sx={ExpandableRowCellDetailTextSx}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {orderItem.orderItemNumber}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}
                >
                  {shipmentInfo.trackingLink ? (
                    <Link
                      color="colors.curiousBlue"
                      underline="hover"
                      target="_blank"
                      rel="noopener"
                      href={shipmentInfo.trackingLink}
                    >
                      <Typography sx={ExpandableRowCellTrackingLinkSx}>
                        {shipmentInfo.trackingNumber ?? '-'}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      color="colors.black"
                      sx={ExpandableRowCellDetailTextSx}
                    >
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography
                    color="colors.black"
                    sx={ExpandableProductTitleRowCellTextSx}
                    data-testid={`ordered-item`}
                  >
                    {getItemProductTitle(orderItem, props.order.orderType.name)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    color="colors.black"
                    sx={ExpandableRowCellDetailTextSx}
                  >
                    {orderItem.serialNumber ?? '-'}
                  </Typography>
                </TableCell>
                {(detailsPage || hasNoShipping) && (
                  <TableCell>
                    <Typography
                      color="colors.black"
                      sx={ExpandableRowCellDetailTextSx}
                    >
                      {getAssetLink(orderItem, isRetrieval)}
                    </Typography>
                  </TableCell>
                )}
                <TableCell
                  sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}
                >
                  <Typography
                    color="colors.black"
                    sx={ExpandableRowCellDetailTextSx}
                  >
                    {getCarrierName(
                      shipmentInfo.shipmentCarrierId,
                      props.carriers
                    )}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}
                >
                  <Stack>
                    <ShipmentStatusPill
                      status={
                        (shipmentInfo?.status as ShipmentStatus) ||
                        (shipmentStatus as ShipmentStatus)
                      }
                      statusText={
                        shipmentInfo?.substatus ||
                        shipmentInfo?.status ||
                        shipmentStatus ||
                        ''
                      }
                      overrideSx={ShipmentPillSx}
                    />
                    {shipmentInfo.substatusDescription && detailsPage && (
                      <Typography sx={ShipmentStatusDescriptionSx}>
                        {shipmentInfo.substatusDescription || ''}
                      </Typography>
                    )}
                    {shipmentStatus === ShipmentStatuses.ATTEMPT_FAILED ||
                    shipmentInfo.status === ShipmentStatuses.ATTEMPT_FAILED ? (
                        <Typography sx={FailedPillTypeSx}>
                        Logistics issue delaying order fulfillment. Please see
                        Order items tab for more information about this
                        exception. If you need help, please contact support.
                        </Typography>
                      ) : null}
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{ display: hasNoShipping ? 'none' : 'table-cell' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {isDelivered && (
                      <CheckCircle
                        sx={{ color: '#7A8450', fontSize: '1rem' }}
                      />
                    )}
                    {shipmentInfo.expectedDeliveryDate ? (
                      <Tooltip
                        title={moment.utc(shipmentInfo.expectedDeliveryDate).format(
                          'MMM D, YYYY'
                        )}
                        placement="top"
                        arrow
                      >
                        <Typography
                          color="colors.black"
                          sx={{
                            ...ExpandableRowCellDetailTextSx,
                            cursor: 'pointer',
                          }}
                        >
                          {moment.utc(shipmentInfo.expectedDeliveryDate).format(
                            'MMM D'
                          )}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        color="colors.black"
                        sx={ExpandableRowCellDetailTextSx}
                      >
                        {isDelivered
                          ? ''
                          : !shipmentInfo.trackingNumber
                            ? '-'
                            : 'Pending'}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    display: !hasNoShipping ? 'none' : 'table-cell',
                    width: '100%',
                  }}
                >
                  <Typography
                    color="colors.black"
                    sx={ExpandableRowCellDetailTextSx}
                  >
                    &nbsp;
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Container>
  );
};

export default ExpandedRowOrderItemDisplay;
